/**
 * Watches the softphone store and announces each call transition. Every rule
 * here is "compare now against last time, speak only on a real change".
 *
 * Subscribed to the store rather than to React, for two reasons: it keeps
 * working while nothing is mounted, which is what lets "Call ended" be
 * announced after the widget closes; and it is notified once per `set()`, in
 * order — React would batch a handler's `updateCall` and `removeCall` into one
 * render and the `endedBy` marker would never be seen.
 */
import type { AnnouncerSnapshot, CallContactResolver } from './index.types';
import { ANALYTICS_EVENT_SOFTPHONE } from 'Modules/softphone/constants/analytics';
import {
  getCallAnsweredAnnouncement,
  getMergeCompletedAnnouncement,
  SOFTPHONE_ANNOUNCEMENTS,
  type SoftphoneAnnouncement,
} from 'Modules/softphone/constants/announcements';
import { CONFERENCE_HELD_ID_PREFIX } from 'Modules/softphone/hooks/useMultipleCallsManager/index.constants';
import { getCallStateFlags } from 'Modules/softphone/resolver';
import { useSoftphoneStore, type SoftphoneStore } from 'Modules/softphone/store';
import { AnnouncementService } from 'Services/announcementService';

export * from './index.types';

/** Until the React layer injects the PersonStore-backed lookup. */
const defaultContactResolver: CallContactResolver = (call) =>
  call.displayName || call.phoneNumber;

export class CallAnnouncer {
  private static instance: CallAnnouncer | null = null;

  private previous: AnnouncerSnapshot | null = null;

  private announcedCallId: string | null = null;

  /** Set by a loss, so only a loss can be followed by "Reconnected". */
  private hasAnnouncedConnectionLoss = false;

  private unsubscribe: (() => void) | null = null;

  private resolveContact: CallContactResolver = defaultContactResolver;

  constructor(
    private readonly announcements: AnnouncementService = AnnouncementService.getInstance()
  ) { }

  static getInstance(): CallAnnouncer {
    if (!CallAnnouncer.instance) {
      CallAnnouncer.instance = new CallAnnouncer();
    }

    return CallAnnouncer.instance;
  }

  /** See {@link CallContactResolver} for why it takes the call. */
  setContactResolver = (resolve: CallContactResolver | null): void => {
    this.resolveContact = resolve ?? defaultContactResolver;
  };

  /** The state at this moment counts as a change, so a call already up is named. */
  start = (): void => {
    if (this.unsubscribe) {
      return;
    }

    this.handle(CallAnnouncer.select(useSoftphoneStore.getState()));
    this.unsubscribe = useSoftphoneStore.subscribe((state) => {
      this.handle(CallAnnouncer.select(state));
    });
  };

  /** Stops watching and forgets what it had seen. */
  stop = (): void => {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.previous = null;
    this.announcedCallId = null;
    this.hasAnnouncedConnectionLoss = false;
  };

  /** The values an announcement decision depends on, flattened. */
  private static select(state: SoftphoneStore): AnnouncerSnapshot {
    const call = state.currentCallId
      ? state.activeCalls.get(state.currentCallId) ?? null
      : null;

    const conference = state.activeConference;
    const isConferenceActive = conference?.conferenceState === 'active';

    // Mirrors useConferenceCallManager: virtual held-conference entries are
    // not participants.
    const participantCount = isConferenceActive
      ? Array.from(state.activeCalls.values()).filter(
        (activeCall) =>
          activeCall.conferenceId === conference?.roomGuid &&
          !activeCall.id.startsWith(CONFERENCE_HELD_ID_PREFIX)
      ).length
      : 0;

    return {
      call,
      status: state.status,
      isConnectionLossFeedbackActive: state.isConnectionLossFeedbackActive,
      isMerging: state.isMerging,
      isConferenceActive,
      participantCount,
    };
  }

  private speak(
    { message, politeness, topic }: SoftphoneAnnouncement,
    event: string = ANALYTICS_EVENT_SOFTPHONE.SR_CALL_STATE_ANNOUNCED
  ): void {
    this.announcements.announce(message, politeness, event, topic);
  }

  private handle(next: AnnouncerSnapshot): void {
    const previous = this.previous;
    this.previous = next;

    this.announceCallEntry(previous, next);
    this.announceCallTeardown(previous, next);
    this.announceToggles(previous, next);
    this.announceConnection(previous, next);
    this.announceMerge(previous, next);
  }

  /**
   * Announce-only: moving focus here would steal it from whoever is typing.
   *
   * Waits for the call to connect. `connecting` and `ringing` are not a call
   * the user is in — announcing "In call with …" while an outbound call is
   * still ringing says something untrue, and says it before the person has
   * picked up. `isConnected` is the same flag the header uses to swap the
   * status text for the running timer, so the words and the UI agree.
   */
  private announceCallEntry(
    _previous: AnnouncerSnapshot | null,
    next: AnnouncerSnapshot
  ): void {
    const { call } = next;

    if (!call) {
      this.announcedCallId = null;
      return;
    }

    if (call.id === this.announcedCallId) {
      return;
    }

    // Promoted only because the call in front of it was transferred away, so
    // it is on its way out too, not somewhere the user arrived.
    if (call.endedBy) {
      return;
    }

    if (!getCallStateFlags(call.state).isConnected) {
      return;
    }

    this.announcedCallId = call.id;

    this.speak(
      getCallAnsweredAnnouncement(this.resolveContact(call)),
      ANALYTICS_EVENT_SOFTPHONE.SR_VIEW_ENTRY_FOCUS
    );
  }

  /** The call is gone, and why decides what is said. */
  private announceCallTeardown(
    previous: AnnouncerSnapshot | null,
    next: AnnouncerSnapshot
  ): void {
    const gone = previous?.call;

    if (!gone || next.call) {
      return;
    }

    this.speak(
      gone.endedBy === 'transferred'
        ? SOFTPHONE_ANNOUNCEMENTS.transferCompleted
        : SOFTPHONE_ANNOUNCEMENTS.callEnded
    );
  }

  /**
   * Scoped to one call: swapping to another must not announce "Call resumed"
   * merely because the new call happens not to be held.
   */
  private announceToggles(
    previous: AnnouncerSnapshot | null,
    next: AnnouncerSnapshot
  ): void {
    const before = previous?.call;
    const after = next.call;

    if (!before || !after || before.id !== after.id) {
      return;
    }

    if (before.isOnHold !== after.isOnHold) {
      this.speak(
        after.isOnHold
          ? SOFTPHONE_ANNOUNCEMENTS.hold
          : SOFTPHONE_ANNOUNCEMENTS.resume,
        ANALYTICS_EVENT_SOFTPHONE.SR_TOGGLE_STATE_ANNOUNCED
      );
    }

    if (before.isMuted !== after.isMuted) {
      this.speak(
        after.isMuted
          ? SOFTPHONE_ANNOUNCEMENTS.mute
          : SOFTPHONE_ANNOUNCEMENTS.unmute,
        ANALYTICS_EVENT_SOFTPHONE.SR_TOGGLE_STATE_ANNOUNCED
      );
    }
  }

  /**
   * The loss follows the feedback flag — the same signal that renders the
   * overlay. The recovery does not: `ConnectionManager` clears that flag on the
   * call-drop timeout and when the last call ends too, both with the transport
   * still down. Registration coming back is the honest signal.
   */
  private announceConnection(
    previous: AnnouncerSnapshot | null,
    next: AnnouncerSnapshot
  ): void {
    const wasReconnecting = previous?.call?.state === 'reconnecting';
    const isReconnecting = next.call?.state === 'reconnecting';

    if (isReconnecting && !wasReconnecting) {
      this.speak(SOFTPHONE_ANNOUNCEMENTS.reconnecting);
    }

    const wasLost = previous?.isConnectionLossFeedbackActive ?? false;

    if (next.isConnectionLossFeedbackActive && !wasLost) {
      this.hasAnnouncedConnectionLoss = true;
      this.speak(SOFTPHONE_ANNOUNCEMENTS.connectionLost);

      return;
    }

    // Guarded by the loss, so registering at start-up says nothing.
    const hasReRegistered =
      next.status === 'registered' && previous?.status !== 'registered';

    if (this.hasAnnouncedConnectionLoss && hasReRegistered) {
      this.hasAnnouncedConnectionLoss = false;
      this.speak(SOFTPHONE_ANNOUNCEMENTS.connectionRestored);
    }
  }

  /**
   * Two signals on purpose: `isMerging` is the attempt, `isConferenceActive`
   * the result, so a failed merge announces the attempt and never the result.
   *
   * They also carry different topics, which is what stops a merge that
   * completes inside the debounce window from swallowing "Merging calls".
   */
  private announceMerge(
    previous: AnnouncerSnapshot | null,
    next: AnnouncerSnapshot
  ): void {
    if (next.isMerging && !previous?.isMerging) {
      this.speak(SOFTPHONE_ANNOUNCEMENTS.mergeStarted);
    }

    if (next.isConferenceActive && !previous?.isConferenceActive) {
      this.speak(getMergeCompletedAnnouncement(next.participantCount));
    }
  }
}
